import React, { useState, useEffect } from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { 
  FileText, 
  Download, 
  Calendar, 
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw
} from 'lucide-react';
import {
  loggingApiService,
  ComplianceReportData,
  ComplianceReportHistoryItem,
  LoggingReportSchedule,
} from '../../../../services/loggingApiService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';

interface ReportGenerationFormProps {
  onGenerate: (reportData: any) => void;
  loading: boolean;
}

/**
 * Report Generation Form Component
 */
const ReportGenerationForm: React.FC<ReportGenerationFormProps> = ({ onGenerate, loading }) => {
  const [formData, setFormData] = useState({
    reportType: 'compliance_summary',
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0], // today
    includeActivities: true,
    includeSecurityEvents: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate(formData);
  };

  const reportTypes = [
    { value: 'compliance_summary', label: 'Compliance Summary' },
    { value: 'security_audit', label: 'Security Audit Report' },
    { value: 'user_activity_analysis', label: 'User Activity Analysis' },
    { value: 'incident_report', label: 'Incident Summary Report' },
    { value: 'access_review', label: 'Access Review Report' }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Generate New Report
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Report Type
            </label>
            <select
              value={formData.reportType}
              onChange={(e) => setFormData(prev => ({ ...prev, reportType: e.target.value }))}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
              required
            >
              {reportTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <Input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                className="text-sm"
                required
              />
            </div>
          </div>
        </div>

        {/* Include Options */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Include in Report
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.includeActivities}
                onChange={(e) => setFormData(prev => ({ ...prev, includeActivities: e.target.checked }))}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">User Activities</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.includeSecurityEvents}
                onChange={(e) => setFormData(prev => ({ ...prev, includeSecurityEvents: e.target.checked }))}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Security Events</span>
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <FileText className="w-4 h-4" />
            <span>{loading ? 'Generating...' : 'Generate Report'}</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

interface ReportHistoryProps {
  reports: ComplianceReportHistoryItem[];
  onDownload: (reportId: string) => void;
}

/**
 * Report History Component
 */
const ReportHistory: React.FC<ReportHistoryProps> = ({ reports, onDownload }) => {
  if (reports.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No reports generated yet</p>
          <p className="text-gray-500 text-sm mt-1">Generated reports will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Report History</h3>
      </div>
      
      <div className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Report Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Date Range
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Generated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {reports.map((report, index) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {report.reportType.replace('_', ' ').toUpperCase()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {report.startDate ? new Date(report.startDate).toLocaleDateString() : '—'} - {report.endDate ? new Date(report.endDate).toLocaleDateString() : '—'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {new Date(report.generatedAt).toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {report.status === 'failed' ? (
                    <Badge variant="destructive" className="flex items-center space-x-1">
                      <AlertTriangle className="w-3 h-3" />
                      <span>FAILED</span>
                    </Badge>
                  ) : (
                    <Badge variant="default" className="flex items-center space-x-1">
                      <CheckCircle className="w-3 h-3" />
                      <span>READY</span>
                    </Badge>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDownload(report.id)}
                    disabled={report.status !== 'ready'}
                    className="flex items-center space-x-1"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Reports Panel Component
 * 
 * Displays compliance and audit report generation
 */
export const ReportsPanel: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<ComplianceReportHistoryItem[]>([]);
  const [lastGeneratedReport, setLastGeneratedReport] = useState<ComplianceReportData | null>(null);
  const [showReportDetails, setShowReportDetails] = useState(false);
  const [schedule, setSchedule] = useState<LoggingReportSchedule | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState<Partial<LoggingReportSchedule>>({});

  useEffect(() => {
    const load = async () => {
      setLoadingHistory(true);
      setError(null);
      try {
        const history = await loggingApiService.getComplianceReportHistory({ page: 1, limit: 50 });
        setReports(history.reports || []);
      } catch (e) {
        console.error('Failed to load report history:', e);
        setError(e instanceof Error ? e.message : 'Failed to load report history');
      } finally {
        setLoadingHistory(false);
      }
    };

    const loadSchedule = async () => {
      try {
        const schedule = await loggingApiService.getReportSchedule();
        setSchedule(schedule);
      } catch (e) {
        console.warn('Failed to load report schedule:', e);
        setSchedule(null);
      }
    };

    load();
    loadSchedule();
  }, []);

  const refreshHistory = async () => {
    setLoadingHistory(true);
    setError(null);
    try {
      const history = await loggingApiService.getComplianceReportHistory({ page: 1, limit: 50 });
      setReports(history.reports || []);
    } catch (e) {
      console.error('Failed to refresh report history:', e);
      setError(e instanceof Error ? e.message : 'Failed to refresh report history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const generateReport = async (formData: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const reportData = await loggingApiService.generateComplianceReport(
        formData.reportType,
        formData.startDate,
        formData.endDate,
        {
          includeActivities: formData.includeActivities,
          includeSecurityEvents: formData.includeSecurityEvents
        }
      );
      
      setLastGeneratedReport(reportData);
      setShowReportDetails(true);

      // Optimistically add to history; then refresh from server (activity stream) to ensure consistency.
      const reportId =
        typeof (reportData as any)?.metadata?.reportId === 'string'
          ? (reportData as any).metadata.reportId
          : `report-${Date.now()}`;
      const generatedAt = reportData.generatedAt || new Date().toISOString();
      const newHistoryItem: ComplianceReportHistoryItem = {
        id: reportId,
        reportType: formData.reportType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        generatedAt,
        status: 'ready',
        includeActivities: !!formData.includeActivities,
        includeSecurityEvents: !!formData.includeSecurityEvents,
      };
      setReports((prev) => [newHistoryItem, ...prev.filter((r) => r.id !== newHistoryItem.id)]);

      // Background refresh (the activity log write may lag slightly)
      setTimeout(() => {
        refreshHistory();
      }, 750);
    } catch (error) {
      console.error('Failed to generate report:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportId: string) => {
    try {
      const report = reports.find((r) => r.id === reportId);
      const data = await loggingApiService.downloadComplianceReport(reportId);

      const pretty = JSON.stringify(data, null, 2);
      const blob = new Blob([pretty], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeType = (report?.reportType || data.reportType || 'report').replace(/[^a-zA-Z0-9_-]+/g, '_');
      const safeStart = report?.startDate || data.dateRange?.startDate || '';
      const safeEnd = report?.endDate || data.dateRange?.endDate || '';
      const fileName = `${safeType}${safeStart ? `_${safeStart}` : ''}${safeEnd ? `_${safeEnd}` : ''}.json`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
      setError(error instanceof Error ? error.message : 'Failed to download report');
    }
  };

  const openScheduleDialog = () => {
    setScheduleDraft(
      schedule || {
        enabled: false,
        frequency: 'weekly',
        reportType: 'security_audit',
        rangeDays: 7,
        includeActivities: false,
        includeSecurityEvents: true,
      }
    );
    setScheduleDialogOpen(true);
  };

  const saveSchedule = async () => {
    setScheduleSaving(true);
    setError(null);
    try {
      const updated = await loggingApiService.updateReportSchedule(scheduleDraft);
      setSchedule(updated);
      setScheduleDialogOpen(false);
    } catch (e) {
      console.error('Failed to update schedule:', e);
      setError(e instanceof Error ? e.message : 'Failed to update schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FileText className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Compliance Reports
          </h3>
        </div>

        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>Last generated: {reports.length > 0 ? new Date(reports[0].generatedAt).toLocaleDateString() : 'Never'}</span>
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshHistory}
            disabled={loadingHistory}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
            <span>{loadingHistory ? 'Refreshing' : 'Refresh'}</span>
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-600 font-medium">Error</p>
          </div>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Report Templates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-lex-accent-soft border border-lex-accent rounded-lg p-4">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-lex-accent-soft rounded-lg">
              <FileText className="w-6 h-6 text-lex-accent" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-lex-accent">Quick Templates</h4>
              <p className="text-xs text-lex-accent">Pre-configured reports</p>
            </div>
          </div>
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-left justify-start text-lex-accent border-lex-accent hover:bg-lex-state-hover"
              onClick={() => generateReport({
                reportType: 'compliance_summary',
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                includeActivities: true,
                includeSecurityEvents: true
              })}
              disabled={loading}
            >
              Monthly Compliance
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-left justify-start text-lex-accent border-lex-accent hover:bg-lex-state-hover"
              onClick={() => generateReport({
                reportType: 'security_audit',
                startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                includeActivities: false,
                includeSecurityEvents: true
              })}
              disabled={loading}
            >
              Weekly Security
            </Button>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-green-100 dark:bg-green-800/50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-green-900 dark:text-green-200">Report Statistics</h4>
              <p className="text-xs text-green-700 dark:text-green-300">Generation overview</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-green-700 dark:text-green-300">Total Generated:</span>
              <span className="text-xs font-medium text-green-900 dark:text-green-200">{reports.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-green-700 dark:text-green-300">This Month:</span>
              <span className="text-xs font-medium text-green-900 dark:text-green-200">
                {reports.filter(r => new Date(r.generatedAt).getMonth() === new Date().getMonth()).length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-green-700 dark:text-green-300">Available:</span>
              <span className="text-xs font-medium text-green-900 dark:text-green-200">
                {reports.filter(r => r.status === 'ready').length}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-800/50 rounded-lg">
              <Settings className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-purple-900 dark:text-purple-200">Scheduled Reports</h4>
              <p className="text-xs text-purple-700 dark:text-purple-300">Automation settings</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-xs text-purple-700 dark:text-purple-300">
              <div className="flex items-center justify-between">
                <span>Auto-generate:</span>
                <Badge variant="outline" className="text-xs">
                  {schedule?.enabled ? (schedule.frequency === 'monthly' ? 'Monthly' : 'Weekly') : 'Disabled'}
                </Badge>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span>Next run:</span>
                <span className="font-medium">
                  {schedule?.enabled && schedule?.nextRun ? new Date(schedule.nextRun).toLocaleDateString() : '—'}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-800/50"
              onClick={openScheduleDialog}
            >
              <Calendar className="w-3 h-3 mr-1" />
              Configure
            </Button>
          </div>
        </div>
      </div>

      {/* Report Generation Form */}
      <ReportGenerationForm onGenerate={generateReport} loading={loading} />

      {/* Report Details Modal */}
      {showReportDetails && lastGeneratedReport && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Report Generated Successfully
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReportDetails(false)}
            >
              ×
            </Button>
          </div>
          
                     <div className="grid grid-cols-2 gap-4 mb-6">
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Report Type</label>
               <p className="text-sm text-gray-900 dark:text-gray-100">{lastGeneratedReport.reportType}</p>
             </div>
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Generated At</label>
               <p className="text-sm text-gray-900 dark:text-gray-100">
                 {new Date(lastGeneratedReport.generatedAt).toLocaleString()}
               </p>
             </div>
           </div>
 
           <div className="bg-gray-50 dark:bg-gray-700 rounded p-4 mb-4">
             <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Report Summary</h4>
             <div className="grid grid-cols-2 gap-4 text-sm">
               <div>
                 <span className="text-gray-600 dark:text-gray-300">Total Activities:</span>
                 <span className="ml-2 font-medium">{lastGeneratedReport.summary.totalActivities}</span>
               </div>
               <div>
                 <span className="text-gray-600 dark:text-gray-300">Security Events:</span>
                 <span className="ml-2 font-medium">{lastGeneratedReport.summary.securityEvents}</span>
               </div>
               <div>
                 <span className="text-gray-600 dark:text-gray-300">Unique Users:</span>
                 <span className="ml-2 font-medium">{lastGeneratedReport.summary.uniqueUsers}</span>
               </div>
               <div>
                 <span className="text-gray-600 dark:text-gray-300">High Risk Activities:</span>
                 <span className="ml-2 font-medium">{lastGeneratedReport.summary.highRiskActivities}</span>
               </div>
             </div>
           </div>
 
           <div className="flex justify-end space-x-2">
             <Button
               variant="outline"
               onClick={() => setShowReportDetails(false)}
             >
               Close
             </Button>
             <Button
               onClick={() => {
                 const reportId =
                   typeof (lastGeneratedReport as any)?.metadata?.reportId === 'string'
                     ? (lastGeneratedReport as any).metadata.reportId
                     : '';
                 if (reportId) {
                   downloadReport(reportId);
                   return;
                 }

                 // Fallback: download the in-memory report payload.
                 try {
                   const pretty = JSON.stringify(lastGeneratedReport, null, 2);
                   const blob = new Blob([pretty], { type: 'application/json' });
                   const url = URL.createObjectURL(blob);
                   const a = document.createElement('a');
                   a.href = url;
                   a.download = `${(lastGeneratedReport.reportType || 'report').replace(/[^a-zA-Z0-9_-]+/g, '_')}.json`;
                   document.body.appendChild(a);
                   a.click();
                   document.body.removeChild(a);
                   URL.revokeObjectURL(url);
                 } catch (e) {
                   console.error('Failed to download report payload:', e);
                 }
               }}
               className="flex items-center space-x-2"
             >
               <Download className="w-4 h-4" />
               <span>Download Report</span>
             </Button>
           </div>
        </div>
      )}

      {/* Report History */}
      <ReportHistory reports={reports} onDownload={downloadReport} />

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Scheduled Reports</DialogTitle>
            <DialogDescription>
              Configure automatic report generation for your firm.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={!!scheduleDraft.enabled}
                onChange={(e) => setScheduleDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              Enable scheduled reports
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Frequency
                </label>
                <select
                  value={(scheduleDraft.frequency as any) || 'weekly'}
                  onChange={(e) => setScheduleDraft((prev) => ({ ...prev, frequency: e.target.value as any }))}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-purple-500 dark:focus:border-purple-400 focus:ring-purple-500 dark:focus:ring-purple-800"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Report Type
                </label>
                <select
                  value={(scheduleDraft.reportType as any) || 'security_audit'}
                  onChange={(e) => setScheduleDraft((prev) => ({ ...prev, reportType: e.target.value }))}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-purple-500 dark:focus:border-purple-400 focus:ring-purple-500 dark:focus:ring-purple-800"
                >
                  <option value="security_audit">Security Audit Report</option>
                  <option value="compliance_summary">Compliance Summary</option>
                  <option value="user_activity_analysis">User Activity Analysis</option>
                  <option value="incident_report">Incident Summary</option>
                  <option value="access_review">Access Review</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={scheduleDraft.includeActivities === true}
                  onChange={(e) => setScheduleDraft((prev) => ({ ...prev, includeActivities: e.target.checked }))}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Include user activities
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={scheduleDraft.includeSecurityEvents !== false}
                  onChange={(e) => setScheduleDraft((prev) => ({ ...prev, includeSecurityEvents: e.target.checked }))}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Include security events
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setScheduleDialogOpen(false)} disabled={scheduleSaving}>
                Cancel
              </Button>
              <Button onClick={saveSchedule} disabled={scheduleSaving}>
                {scheduleSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 
